import { useCallback, useLayoutEffect, useRef } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import type {
  BodyScrollEvent,
  GridApi,
  GridReadyEvent,
  GridState,
} from 'ag-grid-community';
import type { ShipmentRow } from '../../types';

interface UseShipmentGridScrollRestoreArgs {
  gridApiRef: MutableRefObject<GridApi<ShipmentRow> | null>;
  gridLayoutKey: string;
  rowHeight: number;
  tableHostRef: RefObject<HTMLDivElement | null>;
}

export const useShipmentGridScrollRestore = ({
  gridApiRef,
  gridLayoutKey,
  rowHeight,
  tableHostRef,
}: UseShipmentGridScrollRestoreArgs) => {
  const verticalScrollTopRef = useRef(0);
  const pendingRestoreScrollTopRef = useRef<number | null>(null);
  const previousGridLayoutKeyRef = useRef<string | null>(null);
  const restoreTimersRef = useRef<number[]>([]);

  if (previousGridLayoutKeyRef.current === null) {
    previousGridLayoutKeyRef.current = gridLayoutKey;
  } else if (previousGridLayoutKeyRef.current !== gridLayoutKey) {
    pendingRestoreScrollTopRef.current = verticalScrollTopRef.current;
    previousGridLayoutKeyRef.current = gridLayoutKey;
  }

  const initialScrollState: GridState | undefined =
    pendingRestoreScrollTopRef.current && pendingRestoreScrollTopRef.current > 0
      ? {
          scroll: {
            top: pendingRestoreScrollTopRef.current,
            left: 0,
          },
        }
      : undefined;

  const getVerticalScrollElement = useCallback(() => {
    const gridElement = tableHostRef.current;
    if (!gridElement) return null;

    return (
      gridElement.querySelector<HTMLElement>('.ag-body-viewport') ||
      gridElement.querySelector<HTMLElement>('.ag-center-cols-viewport')
    );
  }, [tableHostRef]);

  const captureVerticalScrollTop = useCallback(() => {
    const verticalRange = gridApiRef.current?.getVerticalPixelRange();
    const scrollTop =
      typeof verticalRange?.top === 'number'
        ? verticalRange.top
        : (getVerticalScrollElement()?.scrollTop ?? 0);

    verticalScrollTopRef.current = scrollTop;
    return scrollTop;
  }, [getVerticalScrollElement, gridApiRef]);

  const rememberVerticalScrollTop = useCallback(
    (event: BodyScrollEvent<ShipmentRow>) => {
      verticalScrollTopRef.current = event.api.getVerticalPixelRange().top;
    },
    []
  );

  const clearRestoreTimers = useCallback(() => {
    restoreTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    restoreTimersRef.current = [];
  }, []);

  const restoreVerticalScrollTop = useCallback(
    (scrollTop: number | null = pendingRestoreScrollTopRef.current) => {
      if (!scrollTop || scrollTop <= 0) return;

      const restore = () => {
        const scrollElement = getVerticalScrollElement();
        const gridApi = gridApiRef.current;

        if (!scrollElement && !gridApi) return;

        if (scrollElement) {
          scrollElement.scrollTop = scrollTop;
        }

        const rowIndex = Math.max(0, Math.floor(scrollTop / rowHeight));
        gridApi?.ensureIndexVisible(rowIndex, 'top');
        verticalScrollTopRef.current = scrollTop;
        pendingRestoreScrollTopRef.current = null;
      };

      clearRestoreTimers();
      restore();
      restoreTimersRef.current = [
        window.setTimeout(restore, 0),
        window.setTimeout(restore, 50),
      ];
    },
    [clearRestoreTimers, getVerticalScrollElement, gridApiRef, rowHeight]
  );

  useLayoutEffect(() => {
    restoreVerticalScrollTop();
  }, [gridLayoutKey, restoreVerticalScrollTop]);

  useLayoutEffect(() => clearRestoreTimers, [clearRestoreTimers]);

  const handleGridReady = useCallback(
    (event: GridReadyEvent<ShipmentRow>) => {
      gridApiRef.current = event.api;
      restoreVerticalScrollTop();
    },
    [gridApiRef, restoreVerticalScrollTop]
  );

  return {
    captureVerticalScrollTop,
    handleGridReady,
    initialScrollState,
    rememberVerticalScrollTop,
  };
};
